import { ICertificateViewModel } from '@/@types/client-api';
import { useCallback, useEffect, useState } from 'react';
import LoadingDots from '@/components/ui/Buttons/LoadingDots';
import Icon from '@/components/ui/Icon';
import Shimmer from '../Shimmer';
import SimpleTooltip from '../Tooltip/SimpleTooltip';

type DownloadAllCertificatesLinkProps = {
  isLoading?: boolean;
  pageNumber: number;
  certificates: ICertificateViewModel[];
  onDownload: (certificates: ICertificateViewModel[], fileName?: string) => Promise<void>;
  downloadAllTooltipText?: string;
};

const DownloadAllCertificatesLink = ({
  isLoading = false,
  pageNumber,
  certificates,
  onDownload,
  downloadAllTooltipText
}: DownloadAllCertificatesLinkProps): JSX.Element => {
  const [isProcessing, setProcessing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => setShowTooltip(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  const handleOnClick = useCallback(async () => {
    if (!certificates.length) {
      return;
    }
    try {
      setProcessing(true);
      await onDownload(certificates, `certificates-page-${pageNumber}.zip`);
    } finally {
      setProcessing(false);
    }
  }, [certificates, onDownload, pageNumber]);

  if (isLoading) {
    return <Shimmer className="h-6 w-[92px]" />;
  }

  if (isProcessing) {
    return <LoadingDots color="black" dotSize="small" />;
  }

  const trigger = (
    <a
      data-component={'DownloadAllCertificatesLink'}
      className="flex h-6 cursor-pointer items-center gap-1 text-black hover:text-red"
      onClick={handleOnClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      href="#"
    >
      Download
      <Icon name="download" size="small" />
    </a>
  );

  return (
    <SimpleTooltip open={showTooltip} delayDuration={300}>
      <SimpleTooltip.Trigger asChild>{trigger}</SimpleTooltip.Trigger>
      <SimpleTooltip.Content
        className="text-2 max-w-64 px-2 py-3 font-normal normal-case"
        side="top"
      >
        <SimpleTooltip.Arrow
          className="-my-px border-none fill-white drop-shadow-[0_1px_0_silver]"
          height={6}
          width={12}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: downloadAllTooltipText ?? 'Download all certificates for this page.'
          }}
        ></span>
      </SimpleTooltip.Content>
    </SimpleTooltip>
  );
};

export default DownloadAllCertificatesLink;
